import type React from 'react';
import ActionIcon from '@assets/icons/admin-center/table/ellipsis-vertical.svg';
import { DEFAULT_COLORS, SURFACE_COLORS, TEXT_COLORS } from '@constants/colors.ts';
import { DESKTOP_SIZES, FONT_FAMILY, SIZES } from '@constants/fonts.ts';
import { antdTableFilterStyles } from '@routes/sidebar/admin-center/___shared___/styled.ts';
import {
    CLIENTS_COLUMN_KEY,
    TENANTS_COLUMN_KEY,
} from '@routes/sidebar/admin-center/tenant-client/ui/tenant-client-groupBy-table/constants.ts';
import type { TableStyledProps } from '@routes/sidebar/admin-center/tenant-client/ui/tenant-client-groupBy-table/types.ts';
import type { TableProps } from 'antd';
import { Table } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import styled, { css } from 'styled-components';

const RawStyledTable = styled(Table)<TableStyledProps>`
    ${({ $noData, $noRecordsFound, $groupBy, $pageSizerLabel }) => css`
        /* Scroll containers */
        & :where(.ant-table-container) .ant-table-body,
        & :where(.ant-table-container) .ant-table-content {
            scrollbar-width: thin;
            scrollbar-color: var(--colors-surface-action, #2171c6) var(--colors-border-primary, #e2e8f0);
            scrollbar-gutter: auto;

            &::-webkit-scrollbar {
                height: 8px;
                width: 8px;
            }

            &::-webkit-scrollbar-thumb {
                background-color: var(--colors-surface-action, #2171c6);
                border-radius: 8px;
            }

            &::-webkit-scrollbar-track {
                background: var(--colors-border-primary, #e2e8f0);
            }
            border-right: 1px solid #f0f0f0;
        }

        /* HEADER styles — body/sm/sm-medium */
        & :where(.ant-table) .ant-table-thead > tr > th {
            background: var(--colors-grey-150, #f1f4f8);
            color: var(--colors-text-body, #50566e);
            border-bottom: 1px solid var(--colors-border-primary, #e2e8f0);
            font-family: var(--type-font-family, Poppins), serif;
            font-size: var(--fontsize-body-sm, 14px);
            font-style: normal;
            font-weight: 500;
            line-height: var(--line-height-body-sm, 20px);
        }

        & :where(.ant-table) .ant-table-sticky-holder {
            background: var(--colors-grey-150, #f1f4f8);
            border-bottom: 1px solid var(--colors-border-primary, #e2e8f0);
        }

        & :where(.ant-table).ant-table-bordered .ant-table-thead > tr > th {
            border-color: var(--colors-border-primary, #e2e8f0);
        }

        ${($noData || $noRecordsFound) &&
        css`
            & :where(.ant-table) .ant-table-thead > tr > th:first-child {
                border-bottom-left-radius: 8px !important;
            }

            & :where(.ant-table) .ant-table-thead > tr > th:last-child {
                border-bottom-right-radius: 8px !important;
            }
        `}

        /* Remove vertical dividers for ALL cells when bordered (stable selector, no dev class) */
        &
        .ant-table.ant-table-bordered
        > .ant-table-container
        > :is(.ant-table-content, .ant-table-header, .ant-table-body, .ant-table-summary)
        > table
        > :is(thead, tbody, tfoot)
        > tr
        > :is(th, td) {
            border-inline-end: 0;
            border-right: 0;
        }

        /* 🔒 Hide AntD header hairline pseudo-divider */
        & :where(.ant-table) .ant-table-thead > tr > th::before,
        & :where(.ant-table) .ant-table-thead > tr > td::before {
            content: none !important;
            width: 0;
            background: none;
        }

        /* Add strong separators only at fixed column boundaries */
        & .ant-table .ant-table-container .ant-table-cell-fix-left-last {
            border-inline-end: 3px solid var(--colors-border-primary, #e2e8f0) !important;
            border-right: 3px solid var(--colors-border-primary, #e2e8f0) !important;
        }

        & .ant-table .ant-table-container .ant-table-cell-fix-right-first {
            border-inline-start: 3px solid var(--colors-border-primary, #e2e8f0) !important;
            border-left: 3px solid var(--colors-border-primary, #e2e8f0) !important;
        }

        /* Hide AntD gradient shadows so our borders stay crisp */
        & .ant-table .ant-table-container .ant-table-cell-fix-left-last::after,
        & .ant-table .ant-table-container .ant-table-cell-fix-right-first::after {
            box-shadow: none;
            opacity: 0;
        }

        /* DEFAULT BODY CELLS (active children) */
        & :where(.ant-table) .ant-table-tbody > tr:not(.is-group-row) > td {
            color: var(--colors-text-body, #50566e);
            font-family: var(--type-font-family, Poppins), serif;
            font-size: var(--fontsize-body-sm, 14px);
            font-weight: 400;
            line-height: var(--line-height-body-sm, 20px);
        }

        /* Explicit active class (if you rely on row-active) */
        & :where(.ant-table) .ant-table-tbody > tr.row-active > td {
            color: var(--colors-text-body, #50566e);
            font-family: var(--type-font-family, Poppins), serif;
            font-size: var(--fontsize-body-sm, 14px);
            font-weight: 400;
            line-height: var(--line-height-body-sm, 20px);
        }
        /* Group (section) row look & feel */
        ${$groupBy &&
        css`
            & :where(.ant-table) .ant-table-tbody > tr.is-group-row > td {
                background: var(--primary-100, #f2f8fd);
                color: var(--colors-text-body, #50566e);
                border-top: 1px solid var(--colors-border-primary, #e2e8f0);
                border-bottom: 1px solid var(--colors-border-primary, #e2e8f0);
                font-family: var(--type-font-family, Poppins), serif;
                font-size: var(--fontsize-body-sm, 14px);
                font-weight: 700;
                line-height: var(--line-height-body-sm, 20px);
            }

            & :where(.ant-table) .ant-table-tbody > tr.is-group-row:hover > td {
                background: var(--colors-grey-100, #f2f8fd);
            }

            & :where(.ant-table) .ant-table-tbody > tr.is-group-row .group-title {
                display: inline-block;
                padding: 6px 0;
            }

            & .ant-table .ant-table-container .ant-table-cell-fix-left-last {
                border-right: 2px solid var(--colors-border-primary, #e2e8f0) !important;
                border-inline-end: 2px solid var(--colors-border-primary, #e2e8f0) !important;
            }

            & .ant-table .ant-table-container tr.is-group-row .ant-table-cell-fix-left {
                border-right: 2px solid var(--colors-border-primary, #e2e8f0) !important;
                border-inline-end: 2px solid var(--colors-border-primary, #e2e8f0) !important;
            }

            & .ant-table .ant-table-container tr.is-group-row .ant-table-cell-fix-right-first {
                border-inline-start: 3px solid var(--colors-border-primary, #e2e8f0) !important;
            }
        `}

        /* Pagination */
        && .ant-table-pagination.ant-pagination {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            margin: 24px 0;
            padding: 8px 0;
        }

        && .ant-table-pagination.ant-pagination li {
            border-radius: var(--border-radius-s_8, 8px);
        }

        && .ant-table-pagination .ant-pagination-prev,
        && .ant-table-pagination .ant-pagination-next,
        && .ant-table-pagination .ant-pagination-item > a,
        && .ant-table-pagination .ant-pagination-item-link {
            margin: 0 4px;
            color: ${TEXT_COLORS.action};
            ${FONT_FAMILY.poppins};
            ${DESKTOP_SIZES[SIZES.BODY_MD_MED].fontSize};
            ${DESKTOP_SIZES[SIZES.BODY_MD_MED].fontWeight};
        }

        && .ant-table-pagination .ant-pagination-item-active {
            border-radius: 8px;
            border: 1px solid ${TEXT_COLORS.action};
            background: ${SURFACE_COLORS.page};
        }

        && .ant-table-pagination .ant-pagination-item-active > a {
            color: ${TEXT_COLORS.action};
            ${FONT_FAMILY.poppins};
            ${DESKTOP_SIZES[SIZES.BODY_MD_MED].fontSize};
            ${DESKTOP_SIZES[SIZES.BODY_MD_MED].fontWeight};
        }

        /* right-aligned options + label */
        && .ant-table-pagination .ant-pagination-options {
            margin-left: auto;
            display: flex;
            align-items: center;
        }

        /* label before the size select */
        && .ant-table-pagination .ant-pagination-options::before {
            content: '${$pageSizerLabel ?? 'Clients per page'}';
            margin-right: 8px;
            white-space: nowrap;
            color: ${TEXT_COLORS.body};
            ${FONT_FAMILY.poppins};
            ${DESKTOP_SIZES[SIZES.BODY_SM].fontSize};
            ${DESKTOP_SIZES[SIZES.BODY_SM].fontWeight};
            ${DESKTOP_SIZES[SIZES.BODY_SM].lineHeight};
        }

        && .ant-table-pagination .ant-select-selector {
            border-radius: 8px;
            border: 1px solid ${TEXT_COLORS.disabled};
            background: ${DEFAULT_COLORS.white};
        }

        /* Tenant rows: inactive -> muted text */
        & :where(.ant-table) .ant-table-tbody > tr.row-inactive > td {
            color: var(--colors-text-disabled, #b4b8bf);
            font-family: var(--type-font-family, Poppins), serif;
            font-size: var(--fontsize-body-sm, 14px);
            font-weight: 400;
            line-height: var(--line-height-body-sm, 20px);
        }

        /* Group header when all children inactive AND more than one child */
        & :where(.ant-table) .ant-table-tbody > tr.is-group-row.group-all-inactive > td,
        & :where(.ant-table) .ant-table-tbody > tr.is-group-row.group-all-inactive .group-title {
            color: var(--colors-text-disabled, #b4b8bf);
        }

        /* hide antd's default sort arrows */
        & :where(.ant-table) .ant-table-column-sorters .ant-table-column-sorter {
            display: none !important;
        }

        ${antdTableFilterStyles};
    `}

    td[data-column-key='${TENANTS_COLUMN_KEY}'],
    td[data-column-key='${CLIENTS_COLUMN_KEY}'] {
        cursor: pointer;
    }
`;

export const ActionIconWrapper = styled(ActionIcon)`
    cursor: pointer;
`;

export const ActionsTitleWrapper = styled.div<{ $deleteTenant?: boolean; $disabled?: boolean }>`
    color: ${({ $deleteTenant, $disabled }) => {
        if ($disabled) return 'var(--colors-text-disabled, #b4b8bf)';
        return $deleteTenant ? 'var(--colors-text-error, #DA1E28)' : 'var(--colors-text-body, #50566e)';
    }};
    font-family: var(--type-font-family, Poppins);
    font-size: var(--fontsize-body-sm, 14px);
    font-style: normal;
    font-weight: 400;
    line-height: var(--line-height-body-sm, 20px);
    display: flex;
    padding: var(--spacing-2xs, 4px) 10px;
    align-items: center;
    gap: 10px;
`;

export const StyledTable = RawStyledTable as unknown as <T extends AnyObject = AnyObject>(
    props: TableProps<T> & TableStyledProps,
) => React.JSX.Element;

export const TableStatusWrapper = styled.div<{ $isActive: boolean }>`
    display: flex;
    padding: 4px 8px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: var(--border-radius-round, 48px);
    width: fit-content;
    background: ${({ $isActive }) =>
        $isActive ? 'var(--colors-surface-primary, #fff)' : 'var(--colors-grey-150, #f1f4f8)'};
    border: ${({ $isActive }) =>
        $isActive ? '1px solid var(--colors-text-success, #22A06B)' : '1px solid var(--colors-text-disabled, #B4B8BF)'};
`;

export const TitleStatusWrapper = styled.div<{ $isActive: boolean }>`
    color: ${({ $isActive }) =>
        $isActive ? 'var(--colors-text-success, #22A06B)' : 'var(--colors-text-disabled, #B4B8BF)'};
    font-family: var(--type-font-family, Poppins);
    font-size: 10px;
    font-weight: 500;
    line-height: var(--line-height-body-xs, 16px);
`;

export const TitleColumnWrapper = styled.div`
    display: flex;
    align-items: center;

    svg {
        width: 16px;
        height: 16px;
    }
`;
